import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Download, ExternalLink, Rss, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, API } from '@/api';
import { resolveMediaUrl, getYouTubeEmbedUrl } from '@/utils/helpers';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';
import { SEO } from '@/components/common/SEO';

export const PodcastPage = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const loadEpisodes = async () => {
      try {
        const res = await api.getPodcastEpisodes();
        setEpisodes(res.data);
        if (res.data.length > 0) {
          setSelectedEpisode(res.data[0]);
        }
      } catch (error) {
        console.error('Error loading episodes:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEpisodes();
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleEpisodeSelect = (episode) => {
    setSelectedEpisode(episode);
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && duration) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const rssUrl = `${API}/podcast/rss`;

  return (
    <div data-testid="podcast-page">
      <SEO 
        title="Podcast"
        description="Listen to the New Logical Sports Podcast. Conversations about grassroots soccer, GAA, hurling, and rugby in the United States."
        url="/podcast"
      />
      <PageHeroBanner 
        pageSlug="podcast" 
        fallbackTitle="New Logical Sports Podcast"
        fallbackSubtitle="Conversations about grassroots sports"
      />
      
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* RSS Feed Banner */}
          <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-4 mb-8 border border-orange-500/30 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Rss className="w-6 h-6 text-orange-400" />
              <div>
                <p className="text-white font-medium">Subscribe to our Podcast</p>
                <p className="text-gray-400 text-sm">Available on Apple Podcasts, Spotify & more</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a 
                href={rssUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Rss className="w-4 h-4" /> RSS Feed
              </a>
              <Button variant="outline" className="border-gray-600 text-gray-300" onClick={() => navigator.clipboard.writeText(rssUrl)}>
                Copy Feed URL
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Player / Video Theater */}
            <div className="lg:col-span-2">
              {selectedEpisode ? (
                <div className="bg-gray-800/50 rounded-xl overflow-hidden border border-gray-700">
                  {/* Video Player (if YouTube URL) */}
                  {selectedEpisode.youtube_url ? (
                    <div className="aspect-video bg-black">
                      <iframe
                        src={getYouTubeEmbedUrl(selectedEpisode.youtube_url)}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={selectedEpisode.title}
                      />
                    </div>
                  ) : selectedEpisode.thumbnail ? (
                    <div className="aspect-video bg-gray-900 relative">
                      <img 
                        src={resolveMediaUrl(selectedEpisode.thumbnail)} 
                        alt={selectedEpisode.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <button
                          onClick={handlePlayPause}
                          className="w-20 h-20 rounded-full bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-transform hover:scale-105"
                        >
                          {isPlaying ? (
                            <Pause className="w-10 h-10 text-white" />
                          ) : (
                            <Play className="w-10 h-10 text-white ml-1" />
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-sky-900 to-blue-900 flex items-center justify-center">
                      <button
                        onClick={handlePlayPause}
                        className="w-20 h-20 rounded-full bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-transform hover:scale-105"
                      >
                        {isPlaying ? (
                          <Pause className="w-10 h-10 text-white" />
                        ) : (
                          <Play className="w-10 h-10 text-white ml-1" />
                        )}
                      </button>
                    </div>
                  )}

                  {/* Audio Player Controls */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Badge className="bg-sky-500/20 text-sky-300 mb-2">
                          {selectedEpisode.season ? `S${selectedEpisode.season} ` : ''}
                          {selectedEpisode.episode_number ? `E${selectedEpisode.episode_number}` : 'Episode'}
                        </Badge>
                        <h2 className="text-2xl font-bold text-white">{selectedEpisode.title}</h2>
                        <p className="text-gray-400 text-sm mt-1">{formatDate(selectedEpisode.published_at)}</p>
                      </div>
                      {selectedEpisode.audio_url && (
                        <a
                          href={resolveMediaUrl(selectedEpisode.audio_url)}
                          download
                          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm"
                        >
                          <Download className="w-4 h-4" /> Download MP3
                        </a>
                      )}
                    </div>

                    {/* Audio Progress Bar */}
                    {selectedEpisode.audio_url && (
                      <div className="mb-4">
                        <div 
                          className="h-2 bg-gray-700 rounded-full cursor-pointer"
                          onClick={handleSeek}
                        >
                          <div 
                            className="h-full bg-sky-500 rounded-full"
                            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{formatDuration(currentTime)}</span>
                          <span>{formatDuration(duration || selectedEpisode.duration)}</span>
                        </div>
                      </div>
                    )}

                    <p className="text-gray-300">{selectedEpisode.description}</p>
                  </div>

                  {/* Hidden Audio Element */}
                  {selectedEpisode.audio_url && (
                    <audio
                      ref={audioRef}
                      src={resolveMediaUrl(selectedEpisode.audio_url)}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />
                  )}
                </div>
              ) : (
                <div className="bg-gray-800/50 rounded-xl p-12 text-center border border-gray-700">
                  <p className="text-gray-400">No episodes available yet. Check back soon!</p>
                </div>
              )}
            </div>

            {/* Episode List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">All Episodes</h3>
              
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
                      <div className="h-4 bg-gray-700 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-700 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : episodes.length === 0 ? (
                <div className="bg-gray-800/50 rounded-lg p-6 text-center border border-gray-700">
                  <p className="text-gray-400">No episodes published yet.</p>
                  <p className="text-gray-500 text-sm mt-2">New episodes coming soon!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {episodes.map((episode) => (
                    <div
                      key={episode.id}
                      onClick={() => handleEpisodeSelect(episode)}
                      className={`bg-gray-800/50 rounded-lg p-4 cursor-pointer transition-colors border ${
                        selectedEpisode?.id === episode.id 
                          ? 'border-sky-500 bg-sky-500/10' 
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {episode.thumbnail ? (
                          <img 
                            src={resolveMediaUrl(episode.thumbnail)} 
                            alt="" 
                            className="w-16 h-16 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded bg-gray-700 flex items-center justify-center flex-shrink-0">
                            <Play className="w-6 h-6 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{episode.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(episode.duration)}
                            <span>•</span>
                            {formatDate(episode.published_at)}
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${selectedEpisode?.id === episode.id ? 'text-sky-400' : 'text-gray-600'}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodcastPage;
